import React, { useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import { Users, FileText, MapPin, Calendar, Trash2, Newspaper } from 'lucide-react';
import './Dashboard.css';

export default function Dashboard() {
  const [stats, setStats] = useState({
    users: 0,
    news: 0,
    reports: 0,
    newReports: 0,
    mapPlaces: 0,
    events: 0,
    wasteSchedule: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recentReports, setRecentReports] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Statystyki użytkowników
      const { count: usersCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });

      // Statystyki aktualności (opublikowane)
      const { count: newsCount } = await supabase
        .from('news')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'published');

      // Statystyki zgłoszeń
      const { count: reportsCount } = await supabase
        .from('reports')
        .select('*', { count: 'exact', head: true });

      const { count: newReportsCount } = await supabase
        .from('reports')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'new');

      // Statystyki miejsc na mapie interaktywnej
      const { count: mapPlacesCount } = await supabase
        .from('interactive_map_places')
        .select('*', { count: 'exact', head: true });

      // Statystyki wydarzeń
      const { count: eventsCount } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'published');

      // Statystyki harmonogramu
      const { count: wasteCount } = await supabase
        .from('waste_schedule')
        .select('*', { count: 'exact', head: true });

      // Ostatnie zgłoszenia
      const { data: reports } = await supabase
        .from('reports')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);

      setStats({
        users: usersCount || 0,
        news: newsCount || 0,
        reports: reportsCount || 0,
        newReports: newReportsCount || 0,
        mapPlaces: mapPlacesCount || 0,
        events: eventsCount || 0,
        wasteSchedule: wasteCount || 0,
      });

      setRecentReports(reports || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: 'badge-primary',
      in_progress: 'badge-warning',
      resolved: 'badge-success',
      rejected: 'badge-danger',
    };
    return badges[status] || 'badge-info';
  };

  const getStatusName = (status) => {
    const names = {
      new: 'Nowe',
      in_progress: 'W trakcie',
      resolved: 'Rozwiązane',
      rejected: 'Odrzucone',
    };
    return names[status] || status;
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="dashboard">
      <h2>Przegląd</h2>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon users">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.users}</div>
            <div className="stat-label">Użytkownicy</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon news">
            <Newspaper size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.news}</div>
            <div className="stat-label">Aktualności</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon reports">
            <FileText size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.reports}</div>
            <div className="stat-label">Zgłoszenia</div>
            {stats.newReports > 0 && (
              <div className="stat-badge">{stats.newReports} nowych</div>
            )}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon map-places">
            <MapPin size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.mapPlaces}</div>
            <div className="stat-label">Miejsca na mapie interaktywnej</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon events">
            <Calendar size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.events}</div>
            <div className="stat-label">Wydarzenia</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon waste">
            <Trash2 size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.wasteSchedule}</div>
            <div className="stat-label">Wpisy harmonogramu</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Ostatnie zgłoszenia</h3>
        </div>
        {recentReports.length === 0 ? (
          <div className="empty-state">Brak zgłoszeń</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Tytuł</th>
                <th>Status</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {recentReports.map((report) => (
                <tr key={report.id}>
                  <td>{report.title}</td>
                  <td>
                    <span className={`badge ${getStatusBadge(report.status)}`}>
                      {getStatusName(report.status)}
                    </span>
                  </td>
                  <td>
                    {new Date(report.created_at).toLocaleDateString('pl-PL')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

